import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatMoney } from '../../models/billing-product.type';
import {
  billTotal,
  formatBillDueDate,
  patientAccountBalance,
  PatientBill,
} from '../../models/patient-bill.type';
import { PatientPayment } from '../../models/patient-payment.type';
import { PatientBillService } from '../../services/database/patient-bill.service';
import { PatientPaymentService } from '../../services/database/patient-payment.service';
import { BillDialogComponent } from '../bill-dialog/bill-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { PaymentDialogComponent } from '../payment-dialog/payment-dialog.component';
import { GuideAnchorDirective } from '../../guides/guide-anchor.directive';

@Component({
  selector: 'app-patient-bills',
  templateUrl: './patient-bills.component.html',
  styleUrl: './patient-bills.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, GuideAnchorDirective],
})
export class PatientBillsComponent {
  private billsService = inject(PatientBillService);
  private paymentsService = inject(PatientPaymentService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);

  readonly patientId = input<string | null>(null);
  readonly patientName = input('');

  private accountBody = viewChild<ElementRef<HTMLElement>>('accountBody');

  readonly bills = signal<PatientBill[]>([]);
  readonly payments = signal<PatientPayment[]>([]);
  readonly loading = signal(false);
  readonly refreshing = signal(false);
  /** Holds the section height while a save reloads, so the modal does not collapse. */
  readonly lockedHeight = signal<number | null>(null);
  private loaded = signal(false);
  readonly formatDueDate = formatBillDueDate;
  readonly formatMoney = formatMoney;
  readonly total = billTotal;

  readonly account = computed(() => patientAccountBalance(this.bills(), this.payments()));

  constructor() {
    effect(() => {
      const patientId = this.patientId();
      if (!patientId) {
        this.bills.set([]);
        this.payments.set([]);
        this.loaded.set(false);
        return;
      }
      void this.reload(patientId);
    });
  }

  balanceLabel(): string {
    const difference = this.account().difference;
    if (difference > 0) {
      return 'Pendiente';
    }
    if (difference < 0) {
      return 'Crédito';
    }
    return 'Al corriente';
  }

  canDelete(bill: PatientBill): boolean {
    return this.billsService.canDeleteBill(bill);
  }

  itemSummary(bill: PatientBill): string {
    return bill.items
      .map((item) => {
        const label = item.quantity > 1 ? `${item.name} × ${item.quantity}` : item.name;
        return `${label} (${formatMoney(item.unitPrice)})`;
      })
      .join(', ');
  }

  openCreate(): void {
    const patientId = this.patientId();
    const patientName = this.patientName().trim();
    if (!patientId || !patientName) {
      return;
    }
    this.dialog
      .open(BillDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        data: { patientId, patientName },
      })
      .afterClosed()
      .subscribe((billId) => {
        if (billId) {
          void this.reload(patientId);
        }
      });
  }

  openPayment(): void {
    const patientId = this.patientId();
    const patientName = this.patientName().trim();
    if (!patientId || !patientName) {
      return;
    }
    this.dialog
      .open(PaymentDialogComponent, {
        width: '420px',
        maxWidth: '95vw',
        data: { patientId, patientName },
      })
      .afterClosed()
      .subscribe((paymentId) => {
        if (paymentId) {
          void this.reload(patientId);
        }
      });
  }

  remove(bill: PatientBill): void {
    const patientId = this.patientId();
    if (!patientId) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Eliminar cobro',
          message: 'Este cobro se borrará del expediente del paciente.',
          confirmLabel: 'Eliminar',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          void this.deleteBill(patientId, bill);
        }
      });
  }

  removePayment(payment: PatientPayment): void {
    const patientId = this.patientId();
    if (!patientId) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Eliminar pago',
          message: 'Este pago se borrará de la cuenta del paciente.',
          confirmLabel: 'Eliminar',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          void this.deletePayment(patientId, payment);
        }
      });
  }

  private async reload(patientId: string): Promise<void> {
    const keepVisible = this.loaded();
    if (keepVisible) {
      const height = this.accountBody()?.nativeElement.offsetHeight;
      if (height) {
        this.lockedHeight.set(height);
      }
      this.refreshing.set(true);
    } else {
      this.loading.set(true);
    }
    try {
      const [bills, payments] = await Promise.all([
        this.billsService.listBills(patientId),
        this.paymentsService.listPayments(patientId),
      ]);
      if (this.patientId() !== patientId) {
        return;
      }
      this.bills.set(bills);
      this.payments.set(payments);
      this.loaded.set(true);
    } catch (err) {
      console.error('[PatientBills] list failed', err);
      this.snack.open('No se pudo cargar la cuenta del paciente.', '', { duration: 3000 });
    } finally {
      if (this.patientId() === patientId) {
        this.loading.set(false);
        this.refreshing.set(false);
        this.lockedHeight.set(null);
      }
    }
  }

  private async deleteBill(patientId: string, bill: PatientBill): Promise<void> {
    try {
      await this.billsService.deleteBill(patientId, bill);
      this.bills.update((bills) => bills.filter((item) => item.id !== bill.id));
    } catch (err) {
      console.error('[PatientBills] delete failed', err);
      this.snack.open('No se pudo eliminar el cobro.', '', { duration: 3000 });
    }
  }

  private async deletePayment(patientId: string, payment: PatientPayment): Promise<void> {
    try {
      await this.paymentsService.deletePayment(patientId, payment.id);
      this.payments.update((payments) => payments.filter((item) => item.id !== payment.id));
    } catch (err) {
      console.error('[PatientBills] payment delete failed', err);
      this.snack.open('No se pudo eliminar el pago.', '', { duration: 3000 });
    }
  }
}
